import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceDetailPanel = ({ language, invoice, onClose }) => {
  if (!invoice) return null;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      style: 'currency',
      currency: 'MAD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })?.format(date);
  };

  const labels = {
    title: language === 'fr' ? 'Détails de la facture' : language === 'ar' ? 'تفاصيل الفاتورة' : 'Invoice Details',
    invoiceInfo: language === 'fr' ? 'Informations de facturation' : language === 'ar' ? 'معلومات الفوترة' : 'Invoice Information',
    lineItems: language === 'fr' ? 'Détails des services' : language === 'ar' ? 'تفاصيل الخدمات' : 'Line Items',
    paymentHistory: language === 'fr' ? 'Historique des paiements' : language === 'ar' ? 'سجل المدفوعات' : 'Payment History',
    invoiceNumber: language === 'fr' ? 'N° Facture' : language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number',
    client: language === 'fr' ? 'Client' : language === 'ar' ? 'العميل' : 'Client',
    issueDate: language === 'fr' ? 'Date d\'émission' : language === 'ar' ? 'تاريخ الإصدار' : 'Issue Date',
    dueDate: language === 'fr' ? 'Date d\'échéance' : language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date',
    billingType: language === 'fr' ? 'Type de facturation' : language === 'ar' ? 'نوع الفوترة' : 'Billing Type',
    description: language === 'fr' ? 'Description' : language === 'ar' ? 'الوصف' : 'Description',
    quantity: language === 'fr' ? 'Quantité' : language === 'ar' ? 'الكمية' : 'Quantity',
    rate: language === 'fr' ? 'Tarif' : language === 'ar' ? 'السعر' : 'Rate',
    amount: language === 'fr' ? 'Montant' : language === 'ar' ? 'المبلغ' : 'Amount',
    subtotal: language === 'fr' ? 'Sous-total' : language === 'ar' ? 'المجموع الفرعي' : 'Subtotal',
    tax: language === 'fr' ? 'TVA (20%)' : language === 'ar' ? 'ضريبة القيمة المضافة (20%)' : 'VAT (20%)',
    total: language === 'fr' ? 'Total' : language === 'ar' ? 'الإجمالي' : 'Total',
    paid: language === 'fr' ? 'Payé' : language === 'ar' ? 'مدفوع' : 'Paid',
    balance: language === 'fr' ? 'Solde restant' : language === 'ar' ? 'الرصيد المتبقي' : 'Balance Due',
    date: language === 'fr' ? 'Date' : language === 'ar' ? 'التاريخ' : 'Date',
    method: language === 'fr' ? 'Méthode' : language === 'ar' ? 'الطريقة' : 'Method',
    reference: language === 'fr' ? 'Référence' : language === 'ar' ? 'المرجع' : 'Reference',
    noPayments: language === 'fr' ? 'Aucun paiement enregistré' : language === 'ar' ? 'لا توجد مدفوعات مسجلة' : 'No payments recorded'
  };

  const getBillingTypeLabel = (type) => {
    const types = {
      flat_fee: language === 'fr' ? 'Tarif forfaitaire' : language === 'ar' ? 'رسوم ثابتة' : 'Flat Fee',
      hourly: language === 'fr' ? 'Tarif horaire' : language === 'ar' ? 'رسوم بالساعة' : 'Hourly Rate',
      per_hearing: language === 'fr' ? 'Par audience' : language === 'ar' ? 'لكل جلسة' : 'Per Hearing'
    };
    return types?.[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Invoice Information */}
          <div className="bg-muted rounded-lg p-4 md:p-6 mb-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              {labels?.invoiceInfo}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.invoiceNumber}
                </span>
                <span className="text-sm font-medium text-foreground monospace">
                  {invoice?.invoiceNumber}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.client}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {invoice?.clientName}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.issueDate}
                </span>
                <span className="text-sm font-medium text-foreground monospace">
                  {formatDate(invoice?.issueDate)}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.dueDate}
                </span>
                <span className="text-sm font-medium text-foreground monospace">
                  {formatDate(invoice?.dueDate)}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.billingType}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {getBillingTypeLabel(invoice?.billingType)}
                </span>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              {labels?.lineItems}
            </h3>
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                        {labels?.description}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                        {labels?.quantity}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                        {labels?.rate}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                        {labels?.amount}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoice?.lineItems?.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {item?.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground text-right monospace">
                          {item?.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground text-right monospace">
                          {formatAmount(item?.rate)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground text-right monospace">
                          {formatAmount(item?.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="border-t border-border bg-muted/50 px-4 py-3">
                <div className="flex justify-end">
                  <div className="w-full md:w-80 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{labels?.subtotal}:</span>
                      <span className="font-medium text-foreground monospace">
                        {formatAmount(invoice?.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{labels?.tax}:</span>
                      <span className="font-medium text-foreground monospace">
                        {formatAmount(invoice?.tax)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base md:text-lg border-t border-border pt-2">
                      <span className="font-semibold text-foreground">{labels?.total}:</span>
                      <span className="font-bold text-foreground monospace">
                        {formatAmount(invoice?.amount)}
                      </span>
                    </div>
                    {invoice?.paidAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-success">
                          <span>{labels?.paid}:</span>
                          <span className="font-medium monospace">
                            {formatAmount(invoice?.paidAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between text-base font-semibold text-error">
                          <span>{labels?.balance}:</span>
                          <span className="monospace">
                            {formatAmount(invoice?.amount - invoice?.paidAmount)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              {labels?.paymentHistory}
            </h3>
            {invoice?.paymentHistory && invoice?.paymentHistory?.length > 0 ? (
              <div className="space-y-3">
                {invoice?.paymentHistory?.map((payment, index) => (
                  <div key={index} className="bg-muted rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="CheckCircle" size={20} className="text-success" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground monospace">
                          {formatAmount(payment?.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {payment?.method} • {payment?.reference}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground monospace">
                      {formatDate(payment?.date)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-muted rounded-lg p-8 text-center">
                <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{labels?.noPayments}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailPanel;